import { create } from "zustand";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("ConnectionStore");

// ---------------------------------------------------------------------------
// Connection State Machine
//
//   useOnlineStatus ────┐
//                       ├──▶ useConnectionStore ──▶ ConnectionIndicator
//   visibilitychange ───┘       │      ▲
//                               │      │
//                               ▼      │
//                         reconnectEngine ──▶ reconnectModel()
//                                              duplicate? → stop
// ---------------------------------------------------------------------------

export enum ConnectionState {
  /** Normal operation. No indicator visible. */
  Connected = "Connected",
  /** Brief network blip (0-2s). Subtle amber pulse line. Canvas fully interactive. */
  Degraded = "Degraded",
  /** Actively trying to reconnect (2s-30s). Spinner indicator. Canvas read-only. */
  Reconnecting = "Reconnecting",
  /** Prolonged offline (>30s or retries exhausted). Dot indicator + retry. Canvas read-only. */
  Offline = "Offline",
  /** Just reconnected (1.5s). Checkmark indicator, auto-dismisses. Canvas fully interactive. */
  Restored = "Restored",
}

/** Context about why we entered the current state — used for tab-return messaging. */
export interface ConnectionContext {
  /** Whether the disconnect happened while the tab was hidden. */
  wasTabHidden: boolean;
  /** Number of reconnection attempts so far. */
  attemptCount: number;
  /** Number of pending (unsynced) actions from WASM resolver. */
  pendingActionCount: number;
  /** Applied-but-not-yet-durably-saved edits exist (WASM `has_unsaved_changes`). */
  hasUnsavedChanges: boolean;
  /** A new WASM version is available but the reload is held pending a save. */
  newVersionPending: boolean;
}

interface ConnectionStore {
  state: ConnectionState;
  context: ConnectionContext;

  // --- Transitions ---
  /** Network went offline or WebSocket closed unexpectedly. */
  onConnectionLost: () => void;
  /** Network restored within degraded window AND WebSocket still open. */
  onQuickRestore: () => void;
  /** Degraded debounce timer fired — still offline after 2s. */
  onDegradedTimeout: () => void;
  /** WebSocket connected + model snapshot received. */
  onConnectionRestored: () => void;
  /** Max retries exhausted or snapshot timeout. */
  onRetriesExhausted: () => void;
  /** Manual retry or network change while offline → try again. */
  onRetry: () => void;
  /** Restored auto-dismiss timer fired. */
  onRestoredDismissed: () => void;
  /** Intentional navigation away — clean reset. */
  reset: () => void;

  // --- Context updates ---
  setWasTabHidden: (hidden: boolean) => void;
  setAttemptCount: (count: number) => void;
  setPendingActionCount: (count: number) => void;
  setHasUnsavedChanges: (unsaved: boolean) => void;
  setNewVersionPending: (pending: boolean) => void;
}

const INITIAL_CONTEXT: ConnectionContext = {
  wasTabHidden: false,
  attemptCount: 0,
  pendingActionCount: 0,
  hasUnsavedChanges: false,
  newVersionPending: false,
};

/**
 * Valid state transitions. Used both for runtime validation and testing.
 * Key = current state, Value = set of allowed next states.
 */
export const VALID_TRANSITIONS: Record<
  ConnectionState,
  Set<ConnectionState>
> = {
  [ConnectionState.Connected]: new Set([ConnectionState.Degraded]),
  [ConnectionState.Degraded]: new Set([
    ConnectionState.Connected,
    ConnectionState.Reconnecting,
  ]),
  [ConnectionState.Reconnecting]: new Set([
    ConnectionState.Restored,
    ConnectionState.Offline,
  ]),
  [ConnectionState.Offline]: new Set([ConnectionState.Reconnecting]),
  [ConnectionState.Restored]: new Set([ConnectionState.Connected]),
};

function transition(
  current: ConnectionState,
  next: ConnectionState,
): ConnectionState {
  const allowed = VALID_TRANSITIONS[current];
  if (!allowed.has(next)) {
    log.warning(
      `Invalid connection state transition: ${current} → ${next} (ignored)`,
    );
    return current;
  }
  log.info(`Connection: ${current} → ${next}`);
  return next;
}

export const useConnectionStore = create<ConnectionStore>()((set, _get) => ({
  state: ConnectionState.Connected,
  context: { ...INITIAL_CONTEXT },

  onConnectionLost: () => {
    set((s) => ({
      state: transition(s.state, ConnectionState.Degraded),
      context: { ...s.context, attemptCount: 0 },
    }));
  },

  onQuickRestore: () => {
    set((s) => ({
      state: transition(s.state, ConnectionState.Connected),
      context: { ...s.context, wasTabHidden: false },
    }));
  },

  onDegradedTimeout: () => {
    set((s) => ({
      state: transition(s.state, ConnectionState.Reconnecting),
    }));
  },

  onConnectionRestored: () => {
    set((s) => ({
      state: transition(s.state, ConnectionState.Restored),
    }));
  },

  onRetriesExhausted: () => {
    set((s) => ({
      state: transition(s.state, ConnectionState.Offline),
    }));
  },

  onRetry: () => {
    set((s) => ({
      state: transition(s.state, ConnectionState.Reconnecting),
      context: { ...s.context, attemptCount: 0 },
    }));
  },

  onRestoredDismissed: () => {
    set((s) => ({
      state: transition(s.state, ConnectionState.Connected),
      context: { ...INITIAL_CONTEXT },
    }));
  },

  reset: () => {
    set({ state: ConnectionState.Connected, context: { ...INITIAL_CONTEXT } });
  },

  setWasTabHidden: (hidden) => {
    set((s) => ({ context: { ...s.context, wasTabHidden: hidden } }));
  },

  setAttemptCount: (count) => {
    set((s) => ({ context: { ...s.context, attemptCount: count } }));
  },

  setPendingActionCount: (count) => {
    set((s) => {
      if (s.context.pendingActionCount === count) return s;
      return { context: { ...s.context, pendingActionCount: count } };
    });
  },

  setHasUnsavedChanges: (unsaved) => {
    set((s) => {
      if (s.context.hasUnsavedChanges === unsaved) return s;
      return { context: { ...s.context, hasUnsavedChanges: unsaved } };
    });
  },

  setNewVersionPending: (pending) => {
    set((s) => {
      if (s.context.newVersionPending === pending) return s;
      return { context: { ...s.context, newVersionPending: pending } };
    });
  },
}));

// --- Selectors ---
export const selectConnectionState = (s: ConnectionStore) => s.state;
export const selectConnectionContext = (s: ConnectionStore) => s.context;
export const selectIsReadOnly = (s: ConnectionStore) =>
  s.state === ConnectionState.Reconnecting ||
  s.state === ConnectionState.Offline;

/** Ambient save state derived from durability + connection. */
export enum SaveState {
  /** Everything is durably persisted server-side. */
  Saved = "Saved",
  /** Edits applied locally, server confirmation pending (normal, transient). */
  Saving = "Saving",
  /** Unsaved edits AND the server is unreachable — at risk. */
  NotSaved = "NotSaved",
}

export const selectSaveState = (s: ConnectionStore): SaveState => {
  if (!s.context.hasUnsavedChanges) return SaveState.Saved;
  if (
    s.state === ConnectionState.Reconnecting ||
    s.state === ConnectionState.Offline
  ) {
    return SaveState.NotSaved;
  }
  return SaveState.Saving;
};

export const selectNewVersionPending = (s: ConnectionStore) =>
  s.context.newVersionPending;
